import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Tag, Select, DatePicker, Space, Typography, Row, Col, Statistic, Spin, Button, Modal, Form, Input, Switch, Tabs, Popconfirm, message } from 'antd';
import { YoutubeOutlined, UserOutlined, VideoCameraOutlined, PlusOutlined, DeleteOutlined, ReloadOutlined, TeamOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const statusColors = { public: 'green', unlisted: 'orange', private: 'red', scheduled: 'blue' };

export default function ChannelActivity({ user }) {
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager';
  const perms = user?.permissions || [];
  const canLog = isAdmin || perms.includes('channel_activity');
  const [activeTab, setActiveTab] = useState('log');

  // Video Log state
  const [logData, setLogData] = useState({ logs: [], total: 0, channels: [], staff: [] });
  const [logLoading, setLogLoading] = useState(false);
  const [logFilters, setLogFilters] = useState({ channel_id: '', uploaded_by: '', date_from: '', date_to: '', page: 1 });
  const [addModal, setAddModal] = useState(false);
  const [form] = Form.useForm();

  // Assignments state
  const [assignments, setAssignments] = useState([]);
  const [assignModal, setAssignModal] = useState(false);
  const [assignForm] = Form.useForm();

  // Summary state
  const [summary, setSummary] = useState({ channels: [], staff: [], daily: [] });
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryPeriod, setSummaryPeriod] = useState('week');

  // Fetch video log
  const fetchLog = useCallback(async () => {
    setLogLoading(true);
    try {
      const params = { page: logFilters.page };
      if (logFilters.channel_id) params.channel_id = logFilters.channel_id;
      if (logFilters.uploaded_by) params.uploaded_by = logFilters.uploaded_by;
      if (logFilters.date_from) params.date_from = logFilters.date_from;
      if (logFilters.date_to) params.date_to = logFilters.date_to;
      const res = await api.get('/channel-activity/video-log', { params });
      setLogData(res.data);
    } catch (err) { console.error(err); }
    setLogLoading(false);
  }, [logFilters]);

  // Fetch assignments
  const fetchAssignments = async () => {
    try {
      const res = await api.get('/channel-activity/assignments');
      setAssignments(Array.isArray(res.data) ? res.data : []);
    } catch (err) { console.error(err); }
  };

  // Fetch summary
  const fetchSummary = async () => {
    setSummaryLoading(true);
    try {
      const res = await api.get('/channel-activity/summary', { params: { period: summaryPeriod } });
      setSummary(res.data);
    } catch (err) { console.error(err); }
    setSummaryLoading(false);
  };

  useEffect(() => { fetchLog(); }, [fetchLog]);
  useEffect(() => { fetchAssignments(); }, []);
  useEffect(() => { if (activeTab === 'summary') fetchSummary(); }, [activeTab, summaryPeriod]);

  // Add video log
  const handleAddLog = async (values) => {
    try {
      await api.post('/channel-activity/video-log', {
        ...values,
        upload_date: values.upload_date?.format('YYYY-MM-DD') || dayjs().format('YYYY-MM-DD'),
        upload_time: values.upload_time || dayjs().format('HH:mm:ss'),
        thumbnail_added: values.thumbnail_added ? 1 : 0
      });
      message.success('Video log added!');
      setAddModal(false);
      form.resetFields();
      fetchLog();
    } catch (err) { message.error('Failed'); }
  };

  // Assign staff
  const handleAssign = async (values) => {
    try {
      await api.post('/channel-activity/assignments', values);
      message.success('Staff assigned!');
      setAssignModal(false);
      assignForm.resetFields();
      fetchAssignments();
    } catch (err) { message.error('Failed'); }
  };

  // Delete assignment
  const handleDeleteAssignment = async (id) => {
    try {
      await api.delete(`/channel-activity/assignments/${id}`);
      message.success('Removed!');
      fetchAssignments();
    } catch (err) { message.error('Failed'); }
  };

  // Delete video log
  const handleDeleteLog = async (id) => {
    try {
      await api.delete(`/channel-activity/video-log/${id}`);
      message.success('Deleted!');
      fetchLog();
    } catch (err) { message.error('Failed'); }
  };

  const logColumns = [
    {
      title: 'Date/Time', width: 140,
      render: (_, r) => <span>{dayjs(r.upload_date).format('DD MMM')}{r.upload_time ? ` ${r.upload_time.substring(0,5)}` : ''}</span>
    },
    {
      title: 'Channel', dataIndex: 'channel_name', width: 160,
      render: v => <span><YoutubeOutlined style={{ color: 'red', marginRight: 4 }} />{v}</span>
    },
    { title: 'Video Title', dataIndex: 'video_title', ellipsis: true },
    { title: 'Notes', dataIndex: 'notes', ellipsis: true, width: 150, render: v => v ? <Text type="secondary" style={{ fontSize: 12 }}>{v}</Text> : '-' },
    {
      title: 'Uploaded By', dataIndex: 'uploader_name', width: 130,
      render: v => <span><UserOutlined style={{ marginRight: 4 }} />{v}</span>
    },
    {
      title: 'Status', dataIndex: 'video_status', width: 100,
      render: v => <Tag color={statusColors[v]}>{v}</Tag>
    },
    {
      title: 'Thumbnail', dataIndex: 'thumbnail_added', width: 90,
      render: v => v ? <Tag color="green">Yes</Tag> : <Tag color="orange">No</Tag>
    },
    {
      title: 'URL', dataIndex: 'video_url', width: 60,
      render: v => v ? <a href={v} target="_blank" rel="noopener noreferrer">Open</a> : '-'
    },
    ...(isAdmin ? [{
      title: '', width: 40,
      render: (_, r) => <Popconfirm title="Delete?" onConfirm={() => handleDeleteLog(r.id)}><DeleteOutlined style={{ color: 'red', cursor: 'pointer' }} /></Popconfirm>
    }] : [])
  ];

  const assignColumns = [
    { title: 'Channel', dataIndex: 'channel_name', render: v => <span><YoutubeOutlined style={{ color: 'red', marginRight: 4 }} />{v}</span> },
    { title: 'Staff', dataIndex: 'full_name', render: (v, r) => <span><UserOutlined style={{ marginRight: 4 }} />{v || r.username}</span> },
    { title: 'Role', dataIndex: 'assigned_role', render: v => <Tag color="blue">{v}</Tag> },
    { title: 'Assigned', dataIndex: 'assigned_at', render: v => dayjs(v).format('DD MMM YYYY') },
    ...(isAdmin ? [{
      title: '', width: 40,
      render: (_, r) => <Popconfirm title="Remove?" onConfirm={() => handleDeleteAssignment(r.id)}><DeleteOutlined style={{ color: 'red', cursor: 'pointer' }} /></Popconfirm>
    }] : [])
  ];

  const tabItems = [
    {
      key: 'log',
      label: <span><VideoCameraOutlined /> Video Upload Log</span>,
      children: (
        <>
          <Card size="small" style={{ marginBottom: 16 }}>
            <Space wrap>
              <Select placeholder="All Channels" allowClear style={{ width: 180 }}
                value={logFilters.channel_id || undefined}
                onChange={v => setLogFilters(f => ({ ...f, channel_id: v || '', page: 1 }))}
                options={(logData.channels || []).map(c => ({ value: c.id, label: c.channel_name }))}
              />
              <Select placeholder="All Staff" allowClear style={{ width: 180 }}
                value={logFilters.uploaded_by || undefined}
                onChange={v => setLogFilters(f => ({ ...f, uploaded_by: v || '', page: 1 }))}
                showSearch optionFilterProp="label"
                options={(logData.staff || []).map(s => ({ value: s.id, label: s.full_name || s.username }))}
              />
              <RangePicker onChange={(dates) => {
                if (dates) setLogFilters(f => ({ ...f, date_from: dates[0].format('YYYY-MM-DD'), date_to: dates[1].format('YYYY-MM-DD'), page: 1 }));
                else setLogFilters(f => ({ ...f, date_from: '', date_to: '', page: 1 }));
              }} />
              {canLog && <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModal(true)}>Log Video</Button>}
              <Button icon={<ReloadOutlined />} onClick={fetchLog}>Refresh</Button>
            </Space>
          </Card>
          <Table
            columns={logColumns}
            dataSource={logData.logs || []}
            rowKey="id" size="small" loading={logLoading}
            pagination={{ current: logFilters.page, total: logData.total, pageSize: 50,
              onChange: p => setLogFilters(f => ({ ...f, page: p })), showTotal: t => `${t} videos` }}
            scroll={{ x: 900 }}
          />
        </>
      )
    },
    {
      key: 'assignments',
      label: <span><TeamOutlined /> Channel Assignments</span>,
      children: (
        <>
          {isAdmin && <Button type="primary" icon={<PlusOutlined />} onClick={() => setAssignModal(true)} style={{ marginBottom: 16 }}>Assign Staff</Button>}

          {/* Group by channel */}
          {(() => {
            const channelGroups = {};
            assignments.forEach(a => {
              if (!channelGroups[a.channel_name]) channelGroups[a.channel_name] = [];
              channelGroups[a.channel_name].push(a);
            });
            return Object.entries(channelGroups).map(([ch, staff]) => (
              <Card key={ch} title={<span><YoutubeOutlined style={{ color: 'red', marginRight: 6 }} />{ch}</span>} size="small" style={{ marginBottom: 8 }}>
                <Space wrap>
                  {staff.map(s => (
                    <Tag key={s.id} color="blue" closable={isAdmin} onClose={() => handleDeleteAssignment(s.id)}>
                      <UserOutlined /> {s.full_name} ({s.assigned_role})
                    </Tag>
                  ))}
                </Space>
              </Card>
            ));
          })()}
          {assignments.length === 0 && <Text type="secondary">No assignments yet</Text>}
        </>
      )
    },
    {
      key: 'summary',
      label: <span><YoutubeOutlined /> Channel Summary</span>,
      children: (
        <>
          <Space style={{ marginBottom: 16 }}>
            <Select value={summaryPeriod} onChange={setSummaryPeriod} style={{ width: 150 }}
              options={[{ value: 'week', label: 'Last 7 Days' }, { value: 'month', label: 'This Month' }]}
            />
            <Button icon={<ReloadOutlined />} onClick={fetchSummary}>Refresh</Button>
          </Space>

          {summaryLoading ? <Spin /> : (
            <>
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col xs={8}>
                  <Card><Statistic title="Total Videos" value={(summary.channels || []).reduce((s, c) => s + parseInt(c.total_videos || 0), 0)} prefix={<VideoCameraOutlined />} valueStyle={{ color: '#1890ff' }} /></Card>
                </Col>
                <Col xs={8}>
                  <Card><Statistic title="Active Channels" value={(summary.channels || []).filter(c => parseInt(c.total_videos) > 0).length} prefix={<YoutubeOutlined />} valueStyle={{ color: 'red' }} /></Card>
                </Col>
                <Col xs={8}>
                  <Card><Statistic title="Active Staff" value={(summary.staff || []).length} prefix={<UserOutlined />} valueStyle={{ color: '#52c41a' }} /></Card>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Card title="Channel-wise Uploads" size="small">
                    <Table size="small" pagination={false} dataSource={summary.channels || []} rowKey="id"
                      columns={[
                        { title: 'Channel', dataIndex: 'channel_name' },
                        { title: 'Videos', dataIndex: 'total_videos', render: v => <Tag color="blue">{v}</Tag> },
                        { title: 'Scheduled', dataIndex: 'scheduled_videos', render: v => v > 0 ? <Tag color="cyan">{v}</Tag> : '0' },
                        { title: 'Uploaders', dataIndex: 'uploaders', ellipsis: true }
                      ]}
                    />
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card title="Staff-wise Uploads" size="small">
                    <Table size="small" pagination={false} dataSource={summary.staff || []} rowKey="id"
                      columns={[
                        { title: 'Staff', dataIndex: 'full_name' },
                        { title: 'Videos', dataIndex: 'total_videos', render: v => <Tag color="green">{v}</Tag> },
                        { title: 'Channels', dataIndex: 'channels', ellipsis: true }
                      ]}
                    />
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </>
      )
    }
  ];

  return (
    <div>
      <Title level={3}><YoutubeOutlined style={{ color: 'red' }} /> Channel Activity</Title>
      <Text type="secondary">Track video uploads, staff assignments, and channel performance</Text>

      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} style={{ marginTop: 16 }} />

      {/* Add Video Log Modal */}
      <Modal title="Log Video Upload" open={addModal} onCancel={() => setAddModal(false)} footer={null}>
        <Form form={form} onFinish={handleAddLog} layout="vertical">
          <Form.Item name="channel_id" label="Channel" rules={[{ required: true }]}>
            <Select placeholder="Select channel" options={(logData.channels || []).map(c => ({ value: c.id, label: c.channel_name }))} />
          </Form.Item>
          <Form.Item name="video_title" label="Video Title" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="video_url" label="Video URL"><Input placeholder="https://youtube.com/..." /></Form.Item>
          <Form.Item name="video_status" label="Status" initialValue="public">
            <Select options={[{ value: 'public' }, { value: 'unlisted' }, { value: 'private' }, { value: 'scheduled' }]} />
          </Form.Item>
          <Form.Item name="upload_date" label="Upload Date" initialValue={dayjs()}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="thumbnail_added" label="Thumbnail Added?" valuePropName="checked"><Switch /></Form.Item>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
          <Button type="primary" htmlType="submit" block>Add Log</Button>
        </Form>
      </Modal>

      {/* Assign Staff Modal */}
      <Modal title="Assign Staff to Channel" open={assignModal} onCancel={() => setAssignModal(false)} footer={null}>
        <Form form={assignForm} onFinish={handleAssign} layout="vertical">
          <Form.Item name="channel_id" label="Channel" rules={[{ required: true }]}>
            <Select placeholder="Select channel" options={(logData.channels || []).map(c => ({ value: c.id, label: c.channel_name }))} />
          </Form.Item>
          <Form.Item name="user_id" label="Staff" rules={[{ required: true }]}>
            <Select placeholder="Select staff" showSearch optionFilterProp="label"
              options={(logData.staff || []).map(s => ({ value: s.id, label: s.full_name || s.username }))}
            />
          </Form.Item>
          <Form.Item name="assigned_role" label="Role" initialValue="manager">
            <Select options={[{ value: 'manager', label: 'Manager' }, { value: 'uploader', label: 'Uploader' }, { value: 'editor', label: 'Editor' }]} />
          </Form.Item>
          <Button type="primary" htmlType="submit" block>Assign</Button>
        </Form>
      </Modal>
    </div>
  );
}
